import Papa from "papaparse";

import html2pdf from "html2pdf.js";

//Common
import {
  COD_ERRO_REGRA_NEGOCIO,
  MAX_CSV_SIZE_MB,
  MAX_TAMANHO_ARQUIVO_UPLOAD_MB,
  MAX_TAMANHO_TOTAL_ENVIO_MB,
} from "@/common/constantes";

//Util
import {
  MENSAGENS_INFO,
  showMensagemErro,
  showMensagemInfo,
} from "./mensagens";
import {
  MessageInputFile,
  MessageInputFileWithSelect,
} from "../componentes/Message";

//API
import { api_getLogoTenant } from "../services/api/api_others";

/*
 * Realiza o download de um arquivo CSV.
 */
export function fn_downloadCSV(data, nomeArquivo, showMessage = true) {
  const csv = Papa.unparse(data, {
    delimiter: ";",
    newline: "\r\n",
  });

  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${nomeArquivo}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();

    //document.body.removeChild(link);
    if (link.parentNode == document.body) {
      document.body.removeChild(link);
    }

    if (showMessage) {
      showMensagemInfo(MENSAGENS_INFO.DOWNLOAD_CONCLUIDO);
    }
  }
}

/*
 * Retorna a imagem logo do tenant
 */
export async function fn_getImagemLogoTenant({ tenant, path }) {
  if (tenant) {
    if (tenant?.logo) {
      return `data:image/png;base64,${tenant.logo}`;
    } else {
      return await api_getLogoTenant(tenant.path);
    }
  } else {
    return await api_getLogoTenant(path);
  }
}

/*
 * Converte o arquivo da imagem em base 64
 */
export function fn_convertFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

/*
 * Retorna as extensões permitidas para a imagem de logo do tenant
 */
export function fn_getExtensoesPermitidasLogo() {
  return ["jpg", "jpeg", "png", "svg"];
}

/*
 * Retorna as extensões permitidas para arquivos de importação
 */
export function fn_getExtensoesImportacao() {
  return [".csv"];
}

/*
 * Retorna as extensões permitidas para os anexos da solicitação
 */
export function fn_getExtensoesAnexoSolicitacao() {
  return [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".jfif"];
}

/*
 * Retorna as extensões permitidas para anexar documento do pagamento
 */
export function fn_getExtensoesDocPag() {
  return [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".jfif"];
}

/*
 * Retorna as extensões permitidas para anexar doc de forma de pagamento
 */
export function fn_getExtensoesDocFormaPag() {
  return [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".jfif", ".txt"];
}

/*
 * Valida o tamanho do arquivo selecionado para envio (anexo e documento).
 * Apresenta a mensagem e retorna false se o arquivo for maior que o tamanho máximo.
 */
export function fn_validarTamanhoArquivo(
  file,
  tamanhoMaxMB = MAX_TAMANHO_ARQUIVO_UPLOAD_MB,
) {
  if (file?.size > tamanhoMaxMB * 1024 * 1024) {
    showMensagemErro(
      `O arquivo ${file.name} ultrapassa o tamanho máximo de ${tamanhoMaxMB} MB.`,
    );
    return false;
  }

  return true;
}

/*
 * Valida a soma do tamanho dos arquivos que serão enviados em uma gravação.
 * Apresenta a mensagem e retorna false se a soma for maior que o tamanho total permitido.
 * @param arquivos: arquivos selecionados (os itens vazios são desconsiderados)
 */
export function fn_validarTamanhoTotalEnvio(
  arquivos = [],
  tamanhoMaxMB = MAX_TAMANHO_TOTAL_ENVIO_MB,
) {
  const tamanhoTotal = arquivos.reduce(
    (total, arquivo) => total + (arquivo?.size || 0),
    0,
  );

  if (tamanhoTotal > tamanhoMaxMB * 1024 * 1024) {
    const tamanhoTotalMB = (tamanhoTotal / (1024 * 1024)).toFixed(1);

    showMensagemErro(
      `Os arquivos selecionados somam ${tamanhoTotalMB} MB e o máximo por gravação é ${tamanhoMaxMB} MB. Salve com parte dos arquivos e inclua os demais depois.`,
    );
    return false;
  }

  return true;
}

/*
 * Valida a extensão do arquivo selecionado para envio.
 * A janela de seleção do sistema operacional permite trocar o filtro e escolher arquivo de qualquer extensão,
 * por isso a extensão precisa ser conferida depois da seleção.
 * Apresenta a mensagem e retorna false se a extensão não estiver entre as permitidas.
 * @param extensoes: extensões permitidas, com ou sem o ponto (Ex: [".pdf", ".jpg"]). Sem extensões informadas, aceita qualquer arquivo.
 */
export function fn_validarExtensaoArquivo(file, extensoes = []) {
  if (!file || !extensoes || extensoes.length == 0) {
    return true;
  }

  const extensoesPermitidas = extensoes.map((ext) =>
    (ext.startsWith(".") ? ext : "." + ext).toLowerCase(),
  );

  const nomeArquivo = file.name || "";
  const extensaoArquivo = nomeArquivo.includes(".")
    ? "." + nomeArquivo.split(".").at(-1).toLowerCase()
    : "";

  if (!extensoesPermitidas.includes(extensaoArquivo)) {
    showMensagemErro(
      `O arquivo ${nomeArquivo} não é permitido. Extensões permitidas: ${extensoesPermitidas.join(", ")}.`,
    );
    return false;
  }

  return true;
}

/*
 * Retorna a pasta onde fica os uploads de determinada solicitação
 */
export function fn_getPastaUploadSolicitacao(pathTenant, numSolicitacao) {
  return `${pathTenant}/solicitacoes/${numSolicitacao}`;
}

/*
 * Importa os dados de um arquivo CSV, utilizando o ";" como padrão de delimitador
 */
export function fn_importarCSV(file, delimiter = ";") {
  return new Promise((resolve, reject) => {
    try {
      if (file.size > MAX_CSV_SIZE_MB) {
        reject({
          status: COD_ERRO_REGRA_NEGOCIO,
          message: "O arquivo não pode ser maior que 2 MB",
        });
        return;
      }

      Papa.parse(file, {
        delimiter: delimiter,
        skipEmptyLines: true,
        encoding: "UTF-8",
        complete: function (results) {
          resolve(results);
        },
      });
    } catch (error) {
      reject(error);
    }
  });
}

/*
 * Abre uma modal para informar o arquivo que será importado.
 */
export function fn_openModalFile(extensoes) {
  return MessageInputFile(extensoes);
}

export function fn_openModalFileWithSelect(extensoes, opcoes) {
  return MessageInputFileWithSelect(extensoes, opcoes);
}

/*
 * Gera o arquivo pdf da página html em questão.
 * @param quebraPagina: opcional, regra de quebra de página do html2pdf (Ex: { avoid: ".classe" } para não cortar o elemento entre duas páginas).
 *   Com a quebra de página, a margem é de 0,25 pol: a página fica com medida inteira em pixels (768 x 1008 no retrato),
 *   senão o html2pdf calcula a quebra e corta as páginas com 1px de diferença e, depois de algumas páginas, corta a linha no meio.
 */
export async function fn_gerarPDF(
  nomeArquivo,
  orientacao,
  download = false,
  quebraPagina = null,
) {
  const opt = {
    ...(quebraPagina ? { pagebreak: quebraPagina } : {}),
    margin: quebraPagina ? 0.25 : 0.2,
    filename: nomeArquivo ? `${nomeArquivo}.pdf` : "relatorio.pdf",
    image: { type: "jpeg", quality: 1 },
    html2canvas: {
      //scale: 2,
      //scale: window.devicePixelRatio > 1 ? 1.5 : 1,
      scale: 1.5,
      useCORS: true,
      scrollY: 0,
    },
    jsPDF: {
      unit: "in",
      format: "letter",
      orientation: orientacao || "portrait",
    },
  };

  const el = document.getElementById("print");

  if (!el) throw new Error("Elemento #print não encontrado");

  //salva estilos originais
  const original = {
    height: el.style.height,
    maxHeight: el.style.maxHeight,
    overflow: el.style.overflow,
  };

  //força render completo
  //el.style.height = "auto";
  //Com a quebra de página a altura fica livre: o html2pdf acrescenta espaços para empurrar o elemento para a página seguinte
  //e, com a altura fixa, o final do conteúdo ficaria fora do pdf
  el.style.height = quebraPagina ? "auto" : el.scrollHeight + "px";
  el.style.maxHeight = "none";
  el.style.overflow = "visible";

  //espera o browser recalcular layout (ESSENCIAL)
  await new Promise((r) => setTimeout(r, 400)); //300

  // espera layout
  // await new Promise((r) =>
  //   requestAnimationFrame(() => requestAnimationFrame(r)),
  // );

  // espera imagens
  const images = el.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    }),
  );

  const worker = html2pdf().from(el).set(opt);

  const result = download
    ? worker.save().then(() => {
        showMensagemInfo(MENSAGENS_INFO.DOWNLOAD_CONCLUIDO);
      })
    : worker.outputPdf("bloburl").then((url) => {
        window.open(url);
      });

  await result;

  //restaura layout
  el.style.height = original.height;
  el.style.maxHeight = original.maxHeight;
  el.style.overflow = original.overflow;
}

// export async function fn_gerarPDF(nomeArquivo, orientacao, download = false) {
//   const opt = {
//     margin: 0.2, //{ top: 0.5, right: 0.2, bottom: 0.5, left: 0.2 },
//     filename: nomeArquivo ? `${nomeArquivo}.pdf` : "relatorio.pdf",
//     image: { type: "jpeg", quality: 1 },
//     //html2canvas: { scale: 1 },
//     html2canvas: {
//       scale: 1,
//       useCORS: true,
//     },
//     jsPDF: {
//       unit: "in",
//       format: "letter",
//       orientation: orientacao ? orientacao : "portrait",
//     },
//   };

//   const print = document.getElementById("print");
//   if (!print) {
//     throw new Error("Elemento com id `print` não encontrado");
//   }

//   //html2pdf().from(print).set(opt).save();

//   if (download) {
//     return html2pdf()
//       .from(print)
//       .set(opt)
//       .save()
//       .then(() => {
//         showMensagemInfo(MENSAGENS_INFO.DOWNLOAD_CONCLUIDO);
//       });
//   } else {
//     return html2pdf()
//       .from(print)
//       .set(opt)
//       .outputPdf("bloburl")
//       .then((url) => {
//         window.open(url);
//       });
//   }
// }
